/**
 * The funnel from awareness through to recommendation.
 *
 * Two numbers matter per stage and they answer different questions: the bar
 * shows how much of the market the stage reaches, and the rate beside it shows
 * how many carried over from the stage before. A stage can be wide and still
 * be the leak.
 */

import React from "react";
import PropTypes from "prop-types";
import { FiAlertTriangle } from "react-icons/fi";
import InlineText from "./InlineText";

const pct = (n) => `${Number(n).toFixed(1)}%`;

const ConversionFunnel = ({ brand, stages = [], bottleneckAlert }) => {
  if (!stages.length) return null;

  // Bars are drawn against the widest stage rather than against 100, so a
  // funnel that starts at 40% still reads as a funnel.
  const widest = Math.max(...stages.map((s) => Number(s.value) || 0), 1);

  // The alert belongs against the steepest fall, which is what it describes.
  const worst = stages.reduce(
    (acc, s, i) => {
      const drop =
        s.dropOffRate ??
        (s.retainRate !== undefined ? 100 - s.retainRate : undefined);
      return drop !== undefined && drop > acc.drop ? { i, drop } : acc;
    },
    { i: -1, drop: -Infinity },
  ).i;

  return (
    <section className="lou-funnel" aria-label={`Conversion funnel for ${brand}`}>
      {brand ? <h4 className="lou-funnel__brand">{brand}</h4> : null}

      <ol className="lou-funnel__stages">
        {stages.map((stage, i) => {
          const value = Number(stage.value) || 0;
          const retain = stage.retainRate;
          const drop = stage.dropOffRate;
          return (
            <li className="lou-funnel__stage" key={`${stage.name}-${i}`}>
              <div className="lou-funnel__row">
                <span className="lou-funnel__name">{stage.name}</span>
                <span className="lou-funnel__value">{pct(value)}</span>
              </div>

              <div className="lou-funnel__track">
                <div
                  className="lou-funnel__bar"
                  style={{ width: `${(value / widest) * 100}%` }}
                />
              </div>

              {retain !== undefined || drop !== undefined ? (
                <span className="lou-funnel__rate">
                  {retain !== undefined ? `${pct(retain)} carried over` : null}
                  {retain !== undefined && drop !== undefined ? " · " : null}
                  {drop !== undefined ? `${pct(drop)} lost` : null}
                </span>
              ) : null}

              {bottleneckAlert && i === worst ? (
                <p className="lou-funnel__alert">
                  <FiAlertTriangle size={13} aria-hidden="true" />
                  <span><InlineText text={bottleneckAlert} /></span>
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {/* If no stage carried a rate there is nothing to anchor the alert to,
          so it goes at the end rather than being dropped. */}
      {bottleneckAlert && worst === -1 ? (
        <p className="lou-funnel__alert">
          <FiAlertTriangle size={13} aria-hidden="true" />
          <span><InlineText text={bottleneckAlert} /></span>
        </p>
      ) : null}
    </section>
  );
};

ConversionFunnel.propTypes = {
  brand: PropTypes.string,
  stages: PropTypes.array,
  bottleneckAlert: PropTypes.string,
};

export default ConversionFunnel;
